import React from 'react'
import { useHistory } from 'react-router-dom'
import {
  OneBlinkAppsError,
  submissionService,
  autoSaveService,
} from '@oneblink/apps'
import { useIsMounted } from '@oneblink/apps-react'
import config from '../config'
import useOneBlinkError from './useOneBlinkError'

export default function useSubmitForm(opts?: { autoSaveKey?: string }) {
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false)
  const [submissionError, setSubmissionError] = useOneBlinkError()
  const [formSubmissionResult, setFormSubmissionResult] =
    React.useState<submissionService.FormSubmissionResult>()
  const [
    postSubmissionActionErrorMessage,
    setPostSubmissionActionErrorMessage,
  ] = useOneBlinkError()
  const history = useHistory()
  const isMounted = useIsMounted()

  const handlePostSubmissionAction = React.useCallback(
    async (formSubmissionResult: submissionService.FormSubmissionResult) => {
      try {
        await submissionService.executePostSubmissionAction(
          formSubmissionResult,
          history.push,
        )
      } catch (error) {
        console.warn('Could not execute post submission action', error)
        if (isMounted.current) {
          if (error instanceof Error || error instanceof OneBlinkAppsError) {
            setPostSubmissionActionErrorMessage(error.message)
          }
        }
      }
    },
    [history.push, isMounted, setPostSubmissionActionErrorMessage],
  )

  const onSubmit = React.useCallback(
    ({
      existingTrackingCode,
      draftId,
    }: { existingTrackingCode?: string; draftId?: string } = {}) => {
      return async (newFormSubmission: submissionService.NewFormSubmission) => {
        try {
          setIsSubmitting(true)

          const formSubmission: submissionService.FormSubmission =
            Object.assign({}, newFormSubmission, {
              formsAppId: config.OB_FORMS_APP_ID,
              jobId: null,
              externalId: existingTrackingCode || null,
              draftId: draftId || null,
              preFillFormDataId: null,
            })

          const submissionResult = await submissionService.submit({
            formSubmission,
            isPendingQueueEnabled: true,
            shouldRunExternalIdGeneration: true,
            shouldRunServerValidation: true,
          })

          if (opts?.autoSaveKey) {
            autoSaveService.deleteAutoSaveData(
              newFormSubmission.definition.id,
              opts.autoSaveKey,
            )
          }

          const qsSubmissionValues = newFormSubmission.submission as {
            Stock_Owner_Email?: string
            email?: string
            vetPresent?: string
            Entering_NSW?: string
          }

          if (newFormSubmission.definition.id === config.CATTLETICK_FORM_ID) {
            history.push({
              pathname: `/forms/${newFormSubmission.definition.id}/receipt`,
              search:
                '?' +
                new URLSearchParams({
                  trackingCode: submissionResult.externalId || '',
                  submissionId: submissionResult.submissionId || '',
                  email:
                    qsSubmissionValues.Stock_Owner_Email ||
                    qsSubmissionValues.email ||
                    '',
                  vetDecPending:
                    qsSubmissionValues.vetPresent === 'offsite'
                      ? 'true'
                      : 'false',
                  enteringNsw:
                    qsSubmissionValues.Entering_NSW === 'Yes'
                      ? 'true'
                      : 'false',
                }).toString(),
            })
          } else if (
            newFormSubmission.definition.id === config.VET_DEC_FORM_ID
          ) {
            history.push({
              pathname: `/declaration/submitted`,
              search:
                '?' +
                new URLSearchParams({
                  trackingCode: submissionResult.externalId || '',
                  submissionId: submissionResult.submissionId || '',
                  isInPendingQueue: submissionResult.isInPendingQueue
                    ? 'true'
                    : 'false',
                }).toString(),
            })
          } else {
            setFormSubmissionResult(submissionResult)
          }
        } catch (e) {
          if (e instanceof Error || e instanceof OneBlinkAppsError) {
            setSubmissionError(e)
          }
        } finally {
          setIsSubmitting(false)
        }
      }
    },

    [setSubmissionError, history, opts?.autoSaveKey],
  )

  return {
    onSubmit,
    isSubmitting,
    formSubmissionResult,
    submissionError,
    setSubmissionError,
    handlePostSubmissionAction,
    postSubmissionActionErrorMessage,
  }
}
